import { useState } from "react";
import { Link2, Mail, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { useWaReady } from "@/components/WhatsAppButton";
import { waLinkWithText } from "@/lib/whatsapp";

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; }
  catch { window.prompt("Copy this payment link:", text); return false; }
};

/**
 * "Pay now" link actions for an invoice (invoiceId) or a case balance (applicationId):
 * copy the private link, send it on WhatsApp / email, or disable it and make a new one.
 */
export function PayLinkButtons({ invoiceId, applicationId, testid, showRegenerate = true, compact = false }) {
  useWaReady();
  const [busy, setBusy] = useState("");
  const ref = invoiceId ? { invoice_id: invoiceId } : { application_id: applicationId };
  const origin_url = window.location.origin;
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const copy = async (e, regenerate = false) => {
    stop(e);
    if (busy) return;
    if (regenerate && !window.confirm("Turn off the current pay link and make a new one? The old link will stop working.")) return;
    setBusy(regenerate ? "regen" : "copy");
    try {
      const { data } = await api.post("/pay-links", { ...ref, origin_url, regenerate });
      await copyText(data.url);
      if (!data.online_enabled) toast.warning("Link copied — but online payments are off, so the page will ask the client to contact the office. An admin can turn on Stripe or PayPal in Settings.");
      else if (data.amount_due <= 0) toast.success("Link copied — this is already fully paid, so the page shows \"Paid — thank you\".");
      else toast.success(regenerate ? "New pay link made and copied — the old link no longer works" : "Pay link copied");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(""); }
  };

  const send = async (channel, e) => {
    stop(e);
    if (busy) return;
    const win = channel === "whatsapp" ? window.open("", "_blank") : null;
    setBusy(channel);
    try {
      const { data } = await api.post("/pay-links/send", { ...ref, channel, origin_url });
      if (channel === "whatsapp") {
        const link = waLinkWithText(data.phone, data.text);
        if (win) win.location.href = link; else window.open(link, "_blank", "noopener,noreferrer");
        toast.success("WhatsApp opened with the pay link");
      } else if (data.mode === "sent") {
        toast.success(`Pay link emailed to ${data.to}`);
      } else {
        window.location.href = `mailto:${data.to}?subject=${encodeURIComponent(data.subject)}&body=${encodeURIComponent(data.text)}`;
        toast.success("Pay link drafted in your email app");
      }
    } catch (err) { win?.close(); toast.error(apiErr(err)); } finally { setBusy(""); }
  };

  const btn = "inline-flex items-center gap-1 h-7 px-2 rounded-lg border text-[11px] font-semibold transition-colors disabled:opacity-50";
  const spin = <Loader2 className="h-3 w-3 animate-spin" />;
  return (
    <div className={`inline-flex items-center gap-1 ${compact ? "flex-nowrap" : "flex-wrap"}`} data-testid={testid} onClick={stop}>
      <button type="button" data-testid={`${testid}-copy`} disabled={!!busy} onClick={(e) => copy(e)} title="Copy the client's secure Pay now link"
        className={`${btn} border-primary/30 text-primary bg-primary/5 hover:bg-primary/10`}>
        {busy === "copy" ? spin : <Link2 className="h-3 w-3" />} {compact ? "Pay link" : "Copy pay link"}
      </button>
      <button type="button" data-testid={`${testid}-whatsapp`} disabled={!!busy} onClick={(e) => send("whatsapp", e)} title="Send the pay link on WhatsApp"
        className={`${btn} border-[#25D366]/40 text-[#128C7E] bg-[#25D366]/5 hover:bg-[#25D366]/15`}>
        {busy === "whatsapp" ? spin : <WhatsAppIcon className="h-3 w-3" />}{!compact && " Send"}
      </button>
      <button type="button" data-testid={`${testid}-email`} disabled={!!busy} onClick={(e) => send("email", e)} title="Email the pay link"
        className={`${btn} border-slate-200 text-slate-600 bg-white hover:border-primary/40 hover:text-primary`}>
        {busy === "email" ? spin : <Mail className="h-3 w-3" />}{!compact && " Send"}
      </button>
      {showRegenerate && (
        <button type="button" data-testid={`${testid}-regenerate`} disabled={!!busy} onClick={(e) => copy(e, true)} title="Disable the current link and make a new one"
          className={`${btn} border-slate-200 text-slate-400 bg-white hover:text-slate-700 px-1.5`}>
          {busy === "regen" ? spin : <RefreshCw className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}
